import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { readGuestSession, writeGuestSession } from '../utils/guestMode'

interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  credits_balance: number
  is_superadmin?: boolean
  vk_linked?: boolean
  google_linked?: boolean
  has_password?: boolean
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isGuest: boolean
  setAuth: (user: User, accessToken: string, refreshToken: string) => void
  updateUser: (user: Partial<User>) => void
  enterGuestMode: () => void
  exitGuestMode: () => void
  logout: () => void
}

export const selectCanWrite = (s: AuthState) => s.isAuthenticated && !!s.accessToken
export const selectHasAppAccess = (s: AuthState) => selectCanWrite(s) || s.isGuest

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isGuest: readGuestSession(),

      setAuth: (user, accessToken, refreshToken) => {
        writeGuestSession(false)
        set({ user, accessToken, refreshToken, isAuthenticated: true, isGuest: false })
      },

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      enterGuestMode: () => {
        writeGuestSession(true)
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isGuest: true,
        })
      },

      exitGuestMode: () => {
        writeGuestSession(false)
        set({ isGuest: false })
      },

      logout: () => {
        writeGuestSession(false)
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isGuest: false,
        })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return
        const guest = readGuestSession() && !state.isAuthenticated
        state.isGuest = guest
      },
    }
  )
)